import { z } from "zod";
import { ISSUE_SEVERITIES } from "./core";

/**
 * Yeet enums: the agentic per-page analysis path. Shared by the
 * DB schema, backend MCP tools, the dispatcher worker, and the sandbox agent.
 */

// Lifecycle of one sandbox attempt at one page. `running` until the agent's
// complete_page MCP call (-> completed) or the dispatcher's failure report
// (-> failed). No retry states: a failed attempt fails the frontier row.
export const YEET_ATTEMPT_STATUSES = [
  "running",
  "completed",
  "failed",
] as const;
export const YEET_ATTEMPT_STATUS = {
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
} as const satisfies Record<string, YeetAttemptStatus>;
export const YeetAttemptStatusSchema = z.enum(YEET_ATTEMPT_STATUSES);
export type YeetAttemptStatus = z.infer<typeof YeetAttemptStatusSchema>;

// Why a failed attempt failed, so a swept/failed run names the exact hop that
// broke instead of one opaque "failed". Set only on the failed transition;
// NULL while running or on success. `no_mcp_contact`: the sandbox never reached
// the backend MCP endpoint (bad tunnel/egress) — the sweeper reads
// first_mcp_contact_at to tell this apart from a `timeout` that phoned home then
// hung. `boot_failed`: the dispatcher's sandbox never produced a usable id
// (provision/install error). `agent_error`: the sandbox exited non-zero.
// `budget_exhausted`: a clean exit with no complete_page — the agent ran out of
// its step/token budget. `timeout`: killed at the wall-clock deadline.
export const YEET_ATTEMPT_FAILURE_REASONS = [
  "no_mcp_contact",
  "boot_failed",
  "agent_error",
  "timeout",
  "budget_exhausted",
] as const;
export const YEET_ATTEMPT_FAILURE_REASON = {
  NO_MCP_CONTACT: "no_mcp_contact",
  BOOT_FAILED: "boot_failed",
  AGENT_ERROR: "agent_error",
  TIMEOUT: "timeout",
  BUDGET_EXHAUSTED: "budget_exhausted",
} as const satisfies Record<string, YeetAttemptFailureReason>;
export const YeetAttemptFailureReasonSchema = z.enum(
  YEET_ATTEMPT_FAILURE_REASONS,
);
export type YeetAttemptFailureReason = z.infer<
  typeof YeetAttemptFailureReasonSchema
>;

// Severity vocabulary for agent-submitted findings. Deliberately the same
// value set as public.issues severity (re-exported, not redefined) so yeet
// findings promote into the Signal Record without mapping.
export const FINDING_SEVERITIES = ISSUE_SEVERITIES;
export const FindingSeveritySchema = z.enum(FINDING_SEVERITIES);
export type FindingSeverity = z.infer<typeof FindingSeveritySchema>;

// Lifecycle of one ScrapingBee page fetch. `pending`
// from the admit-time insert until workers/fetch reports back; `ready` points
// at an immutable capture; `failed` carries the provider/evidence error.
export const YEET_FETCH_STATUSES = ["pending", "ready", "failed"] as const;
export const YEET_FETCH_STATUS = {
  PENDING: "pending",
  READY: "ready",
  FAILED: "failed",
} as const satisfies Record<string, YeetFetchStatus>;
export const YeetFetchStatusSchema = z.enum(YEET_FETCH_STATUSES);
export type YeetFetchStatus = z.infer<typeof YeetFetchStatusSchema>;

// How the agent answers one predefined check. `yes` earns the check's points, `no` earns 0
// but stays in the max; both require evidence. `not_applicable` and
// `unable_to_verify` are excluded from the max and require a rationale —
// excluding honesty from the denominator beats punishing it into guessed
// yeses. The backend computes every score from these; the agent never emits
// a number.
export const CHECK_JUDGMENTS = [
  "yes",
  "no",
  "not_applicable",
  "unable_to_verify",
] as const;
export const CHECK_JUDGMENT = {
  YES: "yes",
  NO: "no",
  NOT_APPLICABLE: "not_applicable",
  UNABLE_TO_VERIFY: "unable_to_verify",
} as const satisfies Record<string, CheckJudgment>;
export const CheckJudgmentSchema = z.enum(CHECK_JUDGMENTS);
export type CheckJudgment = z.infer<typeof CheckJudgmentSchema>;

// What kind of evidence a finding cites. `quote` is verbatim page text,
// `selector` a CSS/XPath locator, `url` a link target, `observation` a
// described behavior the agent saw (e.g. after an interaction), `visual` a
// citation of what a screenshot shows (e.g. "hero CTA sits below the fold on
// mobile") — the first-class slot for perceptual judgments pixels reveal that
// the DOM cannot. Not a pgEnum: evidence is stored as
// jsonb, so adding a kind is a contract-only change.
export const EVIDENCE_KINDS = [
  "quote",
  "selector",
  "url",
  "observation",
  "visual",
] as const;
export const EVIDENCE_KIND = {
  QUOTE: "quote",
  SELECTOR: "selector",
  URL: "url",
  OBSERVATION: "observation",
  VISUAL: "visual",
} as const satisfies Record<string, EvidenceKind>;
export const EvidenceKindSchema = z.enum(EVIDENCE_KINDS);
export type EvidenceKind = z.infer<typeof EvidenceKindSchema>;

// Sandbox outcome as observed by the dispatcher. The backend owns the state
// machine: an `exited` report on an attempt the agent never completed via
// complete_page is still recorded as a failure.
export const YEET_DISPATCH_OUTCOMES = ["exited", "failed"] as const;
export const YeetDispatchOutcomeSchema = z.enum(YEET_DISPATCH_OUTCOMES);
export type YeetDispatchOutcome = z.infer<typeof YeetDispatchOutcomeSchema>;
export const YEET_DISPATCH_OUTCOME = {
  EXITED: "exited",
  FAILED: "failed",
} as const satisfies Record<string, YeetDispatchOutcome>;
